// component-page — default skin module
// Token disposition: pass-through (no overrides)
// NO external imports — plain JavaScript only
// Constituent components: run-tabs, run-tab, run-tab-panel, run-badge, run-table,
//   run-table-head, run-table-column, run-table-body, run-table-row, run-table-cell,
//   run-demo-container, run-playground, run-page-nav
// These must be registered by the consuming page before this component renders.

export const styles = `
  :host {
    display: block;
  }

  [part="root"] {
    display: flex;
    flex-direction: column;
    max-width: var(--run-cp-max-width, none);
  }

  [part="hero"] {
    padding: var(--run-cp-hero-padding, 24px 0 20px);
  }

  [part="hero"] h1 {
    margin: 0 0 8px;
    font-size: var(--run-cp-hero-title-size, var(--run-font-size-2xl, 1.5rem));
    font-weight: var(--run-font-semantic-weight-bold, 700);
    color: var(--run-color-text-primary, #0f172a);
  }

  /* Skeleton */
  .skeleton-block {
    display: block;
    background: linear-gradient(
      90deg,
      rgba(0, 0, 0, 0.08) 25%,
      rgba(0, 0, 0, 0.15) 50%,
      rgba(0, 0, 0, 0.08) 75%
    );
    background-size: 200% 100%;
    border-radius: 4px;
    animation: skeleton-shimmer var(--run-motion-semantic-attention-duration, 1500ms) linear infinite;
  }

  @keyframes skeleton-shimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    .skeleton-block {
      animation-duration: calc(var(--run-motion-semantic-attention-duration, 1500ms) * 3);
      background-size: 400% 100%;
    }
  }

  /* Error state */
  .error-state {
    padding: 24px;
  }

  /* Skin stub placeholder */
  .rn-skin-stub {
    display: none;
  }

  /* Style tab — live variant × size matrix + states (JUNE-140) */
  [part="style-matrix"] h2,
  [part="style-states"] h2,
  [part="a11y-demo"] h2 {
    font-size: var(--run-font-size-xl, 1.25rem);
    font-weight: var(--run-font-semantic-weight-heading, 600);
    color: var(--run-color-text-primary, #0f172a);
    margin: 24px 0 12px;
  }

  [part="style-states"] .state-grid {
    display: flex;
    flex-wrap: wrap;
    gap: var(--run-space-semantic-md, 20px);
    align-items: flex-start;
  }

  [part="style-states"] figure,
  [part="style-matrix"] figure {
    margin: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--run-space-semantic-2xs, 5px);
  }

  [part="style-states"] figcaption {
    font-size: var(--run-font-size-sm, 0.875rem);
    color: var(--run-color-text-secondary, #475569);
  }

  [part="a11y-demo"] .a11y-demo-row {
    display: flex;
    flex-wrap: wrap;
    gap: var(--run-space-semantic-sm, 15px);
    padding: var(--run-space-semantic-sm, 15px);
    border: 1px solid var(--run-color-border-subtle, #e2e8f0);
    border-radius: var(--run-radius-semantic-card, 12px);
  }

  [part="a11y-demo"] p {
    color: var(--run-color-text-secondary, #475569);
  }
`;

/** Extract union-of-literals options from a contract type string ("'a' | 'b'"). */
function enumLiterals(type) {
  return [...String(type ?? '').matchAll(/'([^']*)'/g)].map(m => m[1]).filter(v => v !== '');
}

function capitalize(str) {
  const s = String(str ?? '');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Baseline attributes + inner markup for auto-generated live instances, taken
 * from the manifest's FIRST usage example — the one composition guaranteed
 * valid by UAT. Without this, generated instances drop contract-required
 * attributes (run-icon-button's `label` logged console errors from every
 * Style-tab matrix cell — JUNE-145 slice-1 finding). `exclude` lists the
 * attributes the call site overrides (matrix dimensions / state toggles).
 */
function exampleBaseline(m, tag, exclude = []) {
  const html = m?.usage?.examples?.[0]?.html ?? '';
  const open = new RegExp('<' + tag + '([^>]*)>', 'i').exec(html);
  if (!open) return { attrs: '', inner: '' };
  const closeAt = html.indexOf('</' + tag + '>', open.index + open[0].length);
  const inner = closeAt > -1 ? html.slice(open.index + open[0].length, closeAt).trim() : '';
  const parts = [];
  const attrRe = /([\w-]+)(?:="([^"]*)")?/g;
  let mm;
  while ((mm = attrRe.exec(open[1] ?? ''))) {
    if (exclude.includes(mm[1])) continue;
    parts.push(mm[2] != null ? `${mm[1]}="${mm[2]}"` : mm[1]);
  }
  return { attrs: parts.length ? ' ' + parts.join(' ') : '', inner };
}

/** Demo-cell content: example markup wins (icon slots etc.); else a readable text label. */
function cellInner(baselineInner, fallbackText) {
  return baselineInner.includes('<') ? baselineInner : fallbackText;
}

/**
 * HTML-encode a value for safe interpolation into innerHTML.
 * Prevents prose fields that contain tag-like strings (e.g. "<run-button>")
 * from instantiating live custom elements inside the shadow DOM.
 * Intentional HTML fields (usageExamplesHTML, frameworkSamplesFallback) are
 * left unescaped — those produce real run-demo-container / run-tabs markup.
 */
function esc(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Build a run-table from a column spec array and a pre-rendered rows HTML string. */
function runTable(columns, rowsHTML) {
  if (!rowsHTML) return '';
  const colHeaders = columns
    .map(c => `<run-table-column key="${c.key}">${c.label}</run-table-column>`)
    .join('');
  return `
    <run-table>
      <run-table-head>${colHeaders}</run-table-head>
      <run-table-body>${rowsHTML}</run-table-body>
    </run-table>`;
}

/** Build a single run-table-row from an array of already-escaped cell HTML strings. */
function tableRow(cells) {
  return `<run-table-row>${cells.map(c => `<run-table-cell>${c}</run-table-cell>`).join('')}</run-table-row>`;
}

/**
 * Shared tabstrip rendered into every state (skeleton, error, loaded).
 * Each run-tab carries part="tab-link" (or "tab-link tab-link--active" for the
 * active tab), role="tab", data-tab, and aria-current="page" (active only) to
 * preserve the parts API contract and ARIA semantics independent of run-tab's
 * own registration status.
 */
function tabBar(activeTab, labels) {
  const { usage, style, code, accessibility } = labels;
  function tab(key, label) {
    const isActive = activeTab === key;
    return `<run-tab
      id="cp-tab-${key}"
      part="tab-link${isActive ? ' tab-link--active' : ''}"
      panel="${key}"
      role="tab"
      data-tab="${key}"
      ${isActive ? 'aria-current="page"' : ''}
    >${label}</run-tab>`;
  }
  return [
    tab('usage', usage),
    tab('style', style),
    tab('code', code),
    tab('accessibility', accessibility),
  ].join('');
}

export function render(host) {
  const {
    activeTab,
    tabLabelUsage,
    tabLabelStyle,
    tabLabelCode,
    tabLabelAccessibility,
    loadState,
    errorStatus,
    errorMessage,
    manifest,
    src,
  } = host;

  const labels = {
    usage: tabLabelUsage,
    style: tabLabelStyle,
    code: tabLabelCode,
    accessibility: tabLabelAccessibility,
  };

  // ── Skeleton: idle or loading ──────────────────────────────────────────────
  if (loadState === 'idle' || loadState === 'loading') {
    host.shadowRoot.innerHTML = `
      <article part="root">
        <header part="hero">
          <span class="skeleton-block" style="width:40%;height:2rem;margin-bottom:8px;"></span>
          <span class="skeleton-block" style="width:12%;height:1rem;margin-bottom:8px;"></span>
          <span class="skeleton-block" style="width:70%;height:1rem;"></span>
        </header>
        <run-tabs part="tab-bar" active="${activeTab}">
          ${tabBar(activeTab, labels)}
          <run-tab-panel name="usage">
            <div aria-busy="true" aria-label="Loading component documentation">
              <span class="skeleton-block" style="width:100%;height:200px;display:block;"></span>
            </div>
          </run-tab-panel>
          <run-tab-panel name="style"></run-tab-panel>
          <run-tab-panel name="code"></run-tab-panel>
          <run-tab-panel name="accessibility"></run-tab-panel>
        </run-tabs>
      </article>
    `;
    return;
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (loadState === 'error') {
    host.shadowRoot.innerHTML = `
      <article part="root">
        <header part="hero">
          <h1>Error loading documentation</h1>
        </header>
        <run-tabs part="tab-bar" active="${activeTab}">
          ${tabBar(activeTab, labels)}
          <run-tab-panel name="usage">
            <div class="error-state" role="alert">
              <p>Failed to load component documentation.</p>
              <p>URL: ${esc(src)}</p>
              ${errorStatus ? `<p>HTTP status: ${errorStatus}</p>` : ''}
              ${errorMessage ? `<p>${esc(errorMessage)}</p>` : ''}
            </div>
          </run-tab-panel>
          <run-tab-panel name="style"></run-tab-panel>
          <run-tab-panel name="code"></run-tab-panel>
          <run-tab-panel name="accessibility"></run-tab-panel>
        </run-tabs>
      </article>
    `;
    return;
  }

  // ── Loaded state ───────────────────────────────────────────────────────────
  const m = manifest ?? {};
  // Pipeline manifests use `name`; hand-authored manifests may use `title`.
  const title = m.title ?? m.name ?? '';
  const status = m.status ?? '';
  const description = m.description ?? '';
  const nav = m.nav ?? {};
  const hasPrev = nav.prev != null;
  const hasNext = nav.next != null;
  const showPageNav = hasPrev || hasNext;

  // Usage tab — manifest-driven fallback content for override slots
  const usageIntroFallback = m.usage?.intro
    ? `<p>${esc(m.usage.intro)}</p>`
    : '';
  const usageWhenToUseFallback = Array.isArray(m.usage?.whenToUse) && m.usage.whenToUse.length
    ? `<ul>${m.usage.whenToUse.map(item => `<li>${esc(item)}</li>`).join('')}</ul>`
    : '';
  const usageWhenNotToUseFallback = Array.isArray(m.usage?.whenNotToUse) && m.usage.whenNotToUse.length
    ? `<ul>${m.usage.whenNotToUse.map(item => `<li>${esc(item)}</li>`).join('')}</ul>`
    : '';
  // Manifest example schema (showcase-build / RUN-259): { title, description,
  // html }. The live markup goes in run-demo-container's DEFAULT slot (its
  // contract: "the live component markup rendered in the preview area"); the
  // same snippet, escaped, feeds the `code` attribute for the embedded
  // run-code-display; title + description compose the caption slot.
  const usageExamplesHTML = Array.isArray(m.usage?.examples)
    ? m.usage.examples.map(ex => {
        const live = ex.html ?? '';
        const captionText = [ex.title, ex.description].filter(Boolean).map(esc).join(' — ');
        return `<run-demo-container${live ? ` code="${esc(live)}"` : ''}>
          ${live}
          ${captionText ? `<span slot="caption">${captionText}</span>` : ''}
        </run-demo-container>`;
      }).join('\n          ')
    : '';

  // Style tab — live variant × size matrix + boolean states (JUNE-140).
  // Derived from the contract's union-literal and boolean properties, the same
  // inference the playground uses. Sections render only when the contract
  // actually declares the shape — no hollow sections for plain components.
  // NOTE: a brand/mode comparison strip is deliberately ABSENT — brand and
  // dark-mode tokens are emitted at :root scope only, so container-scoped
  // data-run-brand / data-run-theme would render identical instances
  // (JUNE-95 tracks section-scoped emission; add the strip when it lands).
  const _tag = m.tag ?? '';
  const _ctrProps = Array.isArray(m.contract?.properties) ? m.contract.properties : [];
  const _enumProps = _ctrProps.filter(p => p.attribute && p.name !== 'skin' && enumLiterals(p.type).length >= 2);
  const _boolProps = _ctrProps.filter(p => p.attribute && String(p.type ?? '').includes('boolean'));
  const _variantProp = _enumProps.find(p => p.name === 'variant') ?? _enumProps[0] ?? null;
  const _sizeProp = _enumProps.find(p => p.name === 'size' && p !== _variantProp)
    ?? _enumProps.find(p => p !== _variantProp) ?? null;

  let styleMatrixHTML = '';
  if (_tag && _variantProp) {
    const variants = enumLiterals(_variantProp.type);
    const sizes = _sizeProp ? enumLiterals(_sizeProp.type) : [null];
    const matrixBase = exampleBaseline(m, _tag, [
      _variantProp.attribute,
      ...(_sizeProp ? [_sizeProp.attribute] : []),
    ]);
    const columns = [
      { key: 'variant', label: capitalize(_variantProp.name) },
      ...sizes.map(s => ({ key: s ?? 'default', label: s ? esc(s) : 'Default' })),
    ];
    const rowsHTML = variants.map(v => tableRow([
      `<code>${esc(v)}</code>`,
      ...sizes.map(s =>
        `<${_tag}${matrixBase.attrs} ${_variantProp.attribute}="${esc(v)}"${s ? ` ${_sizeProp.attribute}="${esc(s)}"` : ''}>${cellInner(matrixBase.inner, esc(capitalize(v)))}</${_tag}>`
      ),
    ])).join('');
    styleMatrixHTML = `
      <section part="style-matrix">
        <h2>${_sizeProp ? `${capitalize(_variantProp.name)} × ${capitalize(_sizeProp.name)} Matrix` : `${capitalize(_variantProp.name)}s`}</h2>
        ${runTable(columns, rowsHTML)}
      </section>`;
  }

  let styleStatesHTML = '';
  if (_tag && _boolProps.length) {
    const cells = _boolProps.map(b => {
      const base = exampleBaseline(m, _tag, [b.attribute]);
      return `<figure>
        <${_tag}${base.attrs} ${b.attribute}>${cellInner(base.inner, esc(capitalize(b.name)))}</${_tag}>
        <figcaption><code>${esc(b.attribute)}</code></figcaption>
      </figure>`;
    }).join('');
    styleStatesHTML = `
      <section part="style-states">
        <h2>States</h2>
        <div class="state-grid">${cells}</div>
      </section>`;
  }

  // Style tab — CSS custom properties and parts tables.
  // Fallback chain: m.cssProps → m.contract.cssProps (pipeline manifest schema).
  const _cssProps = Array.isArray(m.cssProps) ? m.cssProps : (Array.isArray(m.contract?.cssProps) ? m.contract.cssProps : []);
  const _cssParts = Array.isArray(m.cssParts) ? m.cssParts : (Array.isArray(m.contract?.cssParts) ? m.contract.cssParts : []);

  const cssPropsTableHTML = _cssProps.length
    ? runTable(
        [{ key: 'prop', label: 'Property' }, { key: 'default', label: 'Default' }, { key: 'desc', label: 'Description' }],
        _cssProps.map(p => tableRow([`<code>${esc(p.name)}</code>`, esc(p.default), esc(p.description)])).join('')
      )
    : '';
  const cssPartsTableHTML = _cssParts.length
    ? runTable(
        [{ key: 'part', label: 'Part' }, { key: 'desc', label: 'Description' }],
        _cssParts.map(p => tableRow([`<code>${esc(p.name)}</code>`, esc(p.description)])).join('')
      )
    : '';

  // Code tab — framework samples fallback for override slot
  const frameworks = Array.isArray(m.frameworks) ? m.frameworks : [];
  const frameworkSamplesFallback = frameworks.length
    ? `<run-tabs>
          ${frameworks.map(f =>
              `<run-tab panel="${esc(f.id ?? f.label ?? '')}">${esc(f.label ?? f.id ?? '')}</run-tab>` +
              `<run-tab-panel name="${esc(f.id ?? f.label ?? '')}">` +
              `<run-code-display language="${f.language ?? 'html'}">${f.code ?? ''}</run-code-display>` +
              `</run-tab-panel>`
            ).join('\n          ')}
        </run-tabs>`
    : '';

  // Code tab — API reference tables.
  // Fallback chain: m.api.* → m.contract.* (pipeline manifest) → top-level m.*.
  const apiProperties = Array.isArray(m.api?.properties) ? m.api.properties : (Array.isArray(m.contract?.properties) ? m.contract.properties : []);
  const apiEvents     = Array.isArray(m.api?.events)     ? m.api.events     : (Array.isArray(m.contract?.events)     ? m.contract.events     : []);
  const apiSlots      = Array.isArray(m.api?.slots)      ? m.api.slots      : (Array.isArray(m.contract?.slots)      ? m.contract.slots      : []);
  const apiCssProps   = Array.isArray(m.api?.cssProps)   ? m.api.cssProps   : (Array.isArray(m.contract?.cssProps)   ? m.contract.cssProps   : _cssProps);
  const apiCssParts   = Array.isArray(m.api?.cssParts)   ? m.api.cssParts   : (Array.isArray(m.contract?.cssParts)   ? m.contract.cssParts   : _cssParts);

  const apiPropsTableHTML = apiProperties.length
    ? runTable(
        [{ key: 'prop', label: 'Property' }, { key: 'type', label: 'Type' }, { key: 'default', label: 'Default' }, { key: 'desc', label: 'Description' }],
        apiProperties.map(p => tableRow([`<code>${esc(p.name)}</code>`, esc(p.type), esc(p.default), esc(p.description)])).join('')
      )
    : '';
  const apiEventsTableHTML = apiEvents.length
    ? runTable(
        [{ key: 'event', label: 'Event' }, { key: 'detail', label: 'Detail' }, { key: 'desc', label: 'Description' }],
        apiEvents.map(e => tableRow([`<code>${esc(e.name)}</code>`, esc(e.detail), esc(e.description)])).join('')
      )
    : '';
  const apiSlotsTableHTML = apiSlots.length
    ? runTable(
        [{ key: 'slot', label: 'Slot' }, { key: 'desc', label: 'Description' }],
        apiSlots.map(s => tableRow([`<code>${esc(s.name)}</code>`, esc(s.description)])).join('')
      )
    : '';
  const apiCssPropsTableHTML = apiCssProps.length
    ? runTable(
        [{ key: 'prop', label: 'Property' }, { key: 'default', label: 'Default' }, { key: 'desc', label: 'Description' }],
        apiCssProps.map(p => tableRow([`<code>${esc(p.name)}</code>`, esc(p.default), esc(p.description)])).join('')
      )
    : '';
  const apiCssPartsTableHTML = apiCssParts.length
    ? runTable(
        [{ key: 'part', label: 'Part' }, { key: 'desc', label: 'Description' }],
        apiCssParts.map(p => tableRow([`<code>${esc(p.name)}</code>`, esc(p.description)])).join('')
      )
    : '';

  // Accessibility tab — live try-it demo (JUNE-140): real focusable instances
  // under the keyboard documentation so the focus treatment is experienced,
  // not just described (yensid_v3 "Keyboard & Focus" reference).
  const _a11yBase = _tag ? exampleBaseline(m, _tag, []) : { attrs: '', inner: '' };
  const a11yDemoHTML = _tag
    ? `<section part="a11y-demo">
        <h2>Try It</h2>
        <p>Tab through these live instances to experience the focus treatment and keyboard interactions documented below.</p>
        <div class="a11y-demo-row">
          <${_tag}${_a11yBase.attrs}>${cellInner(_a11yBase.inner, 'First')}</${_tag}>
          <${_tag}${_a11yBase.attrs}>${cellInner(_a11yBase.inner, 'Second')}</${_tag}>
          <${_tag}${_a11yBase.attrs}>${cellInner(_a11yBase.inner, 'Third')}</${_tag}>
        </div>
      </section>`
    : '';

  // Accessibility tab
  const a11y = m.accessibility ?? {};
  const kp = a11y.keyboardPattern;
  const keyboardPatternHTML = kp && (kp.name || kp.url)
    ? `<section><h2>Keyboard Interaction</h2><p>${
        kp.url
          ? `<a href="${kp.url}" target="_blank" rel="noopener">${kp.name || kp.url}</a>`
          : kp.name
      }</p></section>`
    : '';
  const focusManagementHTML = a11y.focusManagement
    ? `<section><h2>Focus Management</h2><p>${esc(a11y.focusManagement)}</p></section>`
    : '';
  const srNarrativeHTML = a11y.screenReaderNarrative
    ? `<section><h2>Screen Reader Experience</h2><p>${esc(a11y.screenReaderNarrative)}</p></section>`
    : '';
  const wcagItems = Array.isArray(a11y.wcag) ? a11y.wcag : [];
  const wcagTableHTML = wcagItems.length
    ? `<section><h2>WCAG Compliance</h2>${
        runTable(
          [{ key: 'criterion', label: 'Criterion' }, { key: 'level', label: 'Level' }, { key: 'how', label: 'How Met' }],
          wcagItems.map(row => tableRow([esc(row.criterion), esc(row.level), esc(row.howMet)])).join('')
        )
      }</section>`
    : '';

  // Page nav
  const pageNavHTML = showPageNav
    ? `<run-page-nav part="page-nav"
        ${hasPrev ? `prev-label="${nav.prev.label ?? ''}" prev-href="${nav.prev.href ?? ''}"` : ''}
        ${hasNext ? `next-label="${nav.next.label ?? ''}" next-href="${nav.next.href ?? ''}"` : ''}
      ></run-page-nav>`
    : '';

  host.shadowRoot.innerHTML = `
    <article part="root">
      <header part="hero">
        ${title ? `<h1>${esc(title)}</h1>` : ''}
        ${status ? `<run-badge variant="neutral" size="sm" pill>${esc(status)}</run-badge>` : ''}
        ${description ? `<p>${esc(description)}</p>` : ''}
      </header>

      <run-tabs part="tab-bar" active="${activeTab}">
        ${tabBar(activeTab, labels)}

        <run-tab-panel name="usage">
          <slot name="usage-intro">${usageIntroFallback}</slot>
          <slot name="usage-when-to-use">${usageWhenToUseFallback}</slot>
          <slot name="usage-when-not-to-use">${usageWhenNotToUseFallback}</slot>
          ${usageExamplesHTML}
          <slot name="usage-examples"></slot>
          <run-playground mode="compact"></run-playground>
          <slot name="usage-guidelines"></slot>
        </run-tab-panel>

        <run-tab-panel name="style">
          <slot name="style-notes"></slot>
          ${styleMatrixHTML}
          ${styleStatesHTML}
          ${cssPropsTableHTML}
          ${cssPartsTableHTML}
          <div class="rn-skin-stub"><!-- TODO RUN-241 --></div>
        </run-tab-panel>

        <run-tab-panel name="code">
          <slot name="code-framework-samples">${frameworkSamplesFallback}</slot>
          <run-playground mode="full"></run-playground>
          ${apiPropsTableHTML}
          ${apiEventsTableHTML}
          ${apiSlotsTableHTML}
          ${apiCssPropsTableHTML}
          ${apiCssPartsTableHTML}
        </run-tab-panel>

        <run-tab-panel name="accessibility">
          ${a11yDemoHTML}
          ${keyboardPatternHTML}
          ${focusManagementHTML}
          ${srNarrativeHTML}
          ${wcagTableHTML}
          <slot name="accessibility-notes"></slot>
        </run-tab-panel>

      </run-tabs>

      ${pageNavHTML}
    </article>
  `;
}
